import { useEffect, useMemo, useRef } from 'react';

import { Map as ImmutableMap } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import type { TagProps } from 'app-components/flux/tags/tagsTypes';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import type { TransactionState } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';
import type { ResourceTxnAction } from 'components/Dialogs/TransactionEditDialog/types';

import { DIALOG_TYPE as TRANSACTION_DETAILS_DIALOG } from 'components/Dialogs/TransactionEditDialog/actions';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

type UpsertTransactionData = {
  transactionId?: string;
  payee?: string;
  amount?: number;
  accountId?: string;
  date?: string;
  category?: string; // category ID
  memo?: string;
  tags?: TagProps[];
  isReviewed?: boolean;
};

type LlmTransactionDialogProps = {
  dialogId: string;
  operation: 'create' | 'update';
  data: UpsertTransactionData;
  callback?: (result: { success: boolean; transactionId?: string; error?: string }) => void;
};

const log = getLogger('components/Dialogs/LlmTransactionDialog');

export const DIALOG_TYPE_LLM_TRANSACTION = 'LLM_TRANSACTION';

const LlmTransactionDialog = ({ dialogId, data, callback }: LlmTransactionDialogProps) => {
  const dispatch = useAppDispatch();

  const settledRef = useRef(false);
  const submitTimeoutRef = useRef<number | null>(null);

  const normalizeIsSuccess = (arg?: unknown): boolean => arg === true;

  const settleOnce = (fn: () => void) => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (submitTimeoutRef.current != null) window.clearTimeout(submitTimeoutRef.current);
    fn();
  };

  const hasTxnId = Boolean(data.transactionId && data.transactionId.length > 0);
  const existingTxn = useAppSelector((state: RootState) =>
    hasTxnId ? transactionsSelectors.getTransactionById(state, data.transactionId as string) : undefined,
  );

  const accountId = data.accountId || existingTxn?.accountId;
  const account = useAppSelector((state: RootState) =>
    accountId ? accountsSelectors.getAccountById(state, accountId) : undefined,
  );
  const isManualAccount = account && !accountsUtils.isAccountConnected(account);

  const categoryId = data.category || (existingTxn?.coa?.type === 'CATEGORY' ? existingTxn.coa.id : undefined);

  const txn = useMemo(() => {
    const base = existingTxn || transactionsTypes.mkCashFlowTransaction();
    const clientId = existingTxn?.clientId || uuidv4().toUpperCase();
    return base.merge({
      id: hasTxnId ? existingTxn?.id : undefined,
      clientId,
      accountId,
      payee: data.payee ?? existingTxn?.payee,
      amount: data.amount ?? existingTxn?.amount,
      postedOn: data.date ?? existingTxn?.postedOn,
      memo: data.memo ?? existingTxn?.memo,
      tags: data.tags ?? existingTxn?.tags,
      coa: categoryId ? { type: 'CATEGORY' as const, id: categoryId } : existingTxn?.coa,
      state: isManualAccount ? ('CLEARED' as TransactionState) : existingTxn?.state,
      isReviewed: data.isReviewed ?? existingTxn?.isReviewed,
    });
  }, [accountId, categoryId, data, existingTxn, hasTxnId, isManualAccount]);

  useEffect(() => {
    const dialogIdDetails = `TRANSACTION_DETAILS_${txn.id || txn.clientId || 'llm'}`;

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          type: TRANSACTION_DETAILS_DIALOG,
          id: dialogIdDetails,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({
            isPrefilled: true,
            txn,
            defaultAccountId: accountId,
            onClose: (isSuccess?: boolean) => {
              dispatch(rootUiActions.removeDialog(dialogIdDetails));

              const didSucceed = normalizeIsSuccess(isSuccess);

              // User cancelled (X / Cancel)
              if (!didSucceed) {
                settleOnce(() => callback?.({ success: false, error: 'User cancelled' }));
                return;
              }

              // Submit initiated: wait for txnActionCallback
              if (submitTimeoutRef.current != null) window.clearTimeout(submitTimeoutRef.current);
              submitTimeoutRef.current = window.setTimeout(() => {
                settleOnce(() =>
                  callback?.({
                    success: false,
                    error: 'Timed out waiting for transaction save confirmation. Check the register to verify.',
                  }),
                );
              }, 5000);
            },

            txnActionCallback: (updated: ResourceTxnAction) => {
              const updatedId = Array.isArray(updated) && updated[0]?.id ? updated[0].id : data.transactionId;
              settleOnce(() => callback?.({ success: true, transactionId: updatedId || txn.id || txn.clientId }));
            },
          }),
        }),
      ),
    );

    dispatch(rootUiActions.removeDialog(dialogId));
    log.debug('LlmTransactionDialog: opened Details dialog', { dialogId, transactionId: data.transactionId });
  }, [accountId, callback, data.transactionId, dialogId, dispatch, txn]);

  return null;
};

export default LlmTransactionDialog;
